import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../auth/AuthContext.tsx';
import '../styles/components.css';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const GITHUB_CLIENT_ID = import.meta.env.VITE_GITHUB_CLIENT_ID || '';

export function LoginPage() {
  const { login, googleAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login({ email, password });
      navigate('/app', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (response: { credential?: string }) => {
    if (!response.credential) return;
    setError('');
    setSocialLoading(true);
    try {
      await googleAuth(response.credential);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google login failed');
    } finally {
      setSocialLoading(false);
    }
  };

  const handleGitHub = () => {
    const redirectUri = `${window.location.origin}/auth/github/callback`;
    window.location.href = `https://github.com/login/oauth/authorize?client_id=${GITHUB_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=user:email`;
  };

  const socialButtons = (
    <div className="ds-auth-social">
      {GOOGLE_CLIENT_ID && (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Google sign-in failed')}
            theme="filled_black"
            shape="pill"
            width="356"
          />
        </GoogleOAuthProvider>
      )}
      {GITHUB_CLIENT_ID && (
        <button type="button" className="ds-auth-social__btn" onClick={handleGitHub} disabled={submitting || socialLoading}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
          </svg>
          Sign in with GitHub
        </button>
      )}
    </div>
  );

  return (
    <div className="ds-auth-page">
      <div className="ds-auth-card">
        <div className="ds-auth-card__head">
          <Link to="/" className="ds-auth-card__brand">Sketchflow</Link>
          <h1 className="ds-auth-card__title">Welcome back</h1>
          <p className="ds-auth-card__subtitle">Sign in to your account</p>
        </div>

        {error && <div className="ds-auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="ds-auth-form">
          <div className="ds-field">
            <span className="ds-field__label">Email</span>
            <input
              className="ds-field__input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>
          <div className="ds-field">
            <span className="ds-field__label">Password</span>
            <input
              className="ds-field__input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 8 characters"
              required
              minLength={8}
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            className="ds-btn ds-btn--primary ds-auth-form__submit"
            disabled={submitting || socialLoading}
          >
            {submitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        {(GOOGLE_CLIENT_ID || GITHUB_CLIENT_ID) && (
          <>
            <div className="ds-auth-divider"><span>or</span></div>
            {socialButtons}
          </>
        )}

        <p className="ds-auth-card__footer">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="ds-auth-link">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
